/**
 * Role-Based Access Control (RBAC) Permission System
 * Story 5.10: Role-Based Access Control (NFR33)
 *
 * Defines admin roles, permissions, and helper functions for access control.
 */

/**
 * Admin roles in the system
 */
export type AdminRole = 'super_admin' | 'admin' | 'moderator' | 'support';

/**
 * All available permissions in the system
 */
export type Permission =
  // Users
  | 'users.view'
  | 'users.edit'
  | 'users.impersonate'
  // Bots
  | 'bots.view'
  | 'bots.test'
  | 'bots.transfer'
  // Moderation
  | 'moderation.flag'
  | 'moderation.suspend'
  // Costs
  | 'costs.view'
  | 'costs.export'
  // Analytics
  | 'analytics.view'
  | 'analytics.export'
  // Audit
  | 'audit.view'
  | 'audit.export'
  // Team
  | 'team.view'
  | 'team.invite'
  | 'team.manage';

/**
 * Permission categories for grouping in UI
 */
export type PermissionCategory =
  | 'users'
  | 'bots'
  | 'moderation'
  | 'costs'
  | 'analytics'
  | 'audit'
  | 'team';

/**
 * Permission metadata for display purposes
 */
export interface PermissionInfo {
  id: Permission;
  name: string;
  description: string;
  category: PermissionCategory;
}

/**
 * All permissions with their metadata
 */
export const PERMISSIONS: Record<Permission, PermissionInfo> = {
  'users.view': {
    id: 'users.view',
    name: 'View Users',
    description: 'View all registered user accounts',
    category: 'users',
  },
  'users.edit': {
    id: 'users.edit',
    name: 'Edit Users',
    description: 'Edit user account details',
    category: 'users',
  },
  'users.impersonate': {
    id: 'users.impersonate',
    name: 'Act as Owner',
    description: 'Open a bot in the creator app as its owner (impersonation)',
    category: 'users',
  },
  'bots.view': {
    id: 'bots.view',
    name: 'View Bots',
    description: 'View all created bots',
    category: 'bots',
  },
  'bots.test': {
    id: 'bots.test',
    name: 'Test Bots',
    description: 'Test any bot for support purposes',
    category: 'bots',
  },
  'bots.transfer': {
    id: 'bots.transfer',
    name: 'Transfer Bots',
    description: 'Transfer bot ownership to another user',
    category: 'bots',
  },
  'moderation.flag': {
    id: 'moderation.flag',
    name: 'Flag Bots',
    description: 'Flag bots for spam or abuse',
    category: 'moderation',
  },
  'moderation.suspend': {
    id: 'moderation.suspend',
    name: 'Suspend Bots',
    description: 'Suspend bot accounts',
    category: 'moderation',
  },
  'costs.view': {
    id: 'costs.view',
    name: 'View Costs',
    description: 'View cost tracking data per bot',
    category: 'costs',
  },
  'costs.export': {
    id: 'costs.export',
    name: 'Export Costs',
    description: 'Export cost data to CSV',
    category: 'costs',
  },
  'analytics.view': {
    id: 'analytics.view',
    name: 'View Analytics',
    description: 'View platform-wide analytics',
    category: 'analytics',
  },
  'analytics.export': {
    id: 'analytics.export',
    name: 'Export Analytics',
    description: 'Export analytics data to CSV',
    category: 'analytics',
  },
  'audit.view': {
    id: 'audit.view',
    name: 'View Audit Log',
    description: 'View admin audit logs',
    category: 'audit',
  },
  'audit.export': {
    id: 'audit.export',
    name: 'Export Audit Log',
    description: 'Export audit logs to CSV',
    category: 'audit',
  },
  'team.view': {
    id: 'team.view',
    name: 'View Team',
    description: 'View all admin users',
    category: 'team',
  },
  'team.invite': {
    id: 'team.invite',
    name: 'Invite Admins',
    description: 'Invite new admin users',
    category: 'team',
  },
  'team.manage': {
    id: 'team.manage',
    name: 'Manage Team',
    description: 'Change roles and deactivate admins',
    category: 'team',
  },
};

/**
 * Role-to-permissions mapping
 * Defines which permissions each role has access to
 */
export const ROLE_PERMISSIONS: Record<AdminRole, Permission[]> = {
  super_admin: [
    'users.view',
    'users.edit',
    'users.impersonate',
    'bots.view',
    'bots.test',
    'bots.transfer',
    'moderation.flag',
    'moderation.suspend',
    'costs.view',
    'costs.export',
    'analytics.view',
    'analytics.export',
    'audit.view',
    'audit.export',
    'team.view',
    'team.invite',
    'team.manage',
  ],
  admin: [
    'users.view',
    'users.edit',
    'users.impersonate',
    'bots.view',
    'bots.test',
    'bots.transfer',
    'moderation.flag',
    'moderation.suspend',
    'costs.view',
    'costs.export',
    'analytics.view',
    'analytics.export',
    'audit.view',
  ],
  moderator: ['bots.view', 'bots.test', 'moderation.flag', 'moderation.suspend'],
  support: ['users.view', 'bots.view', 'bots.test'],
};

/**
 * Role display names and descriptions
 */
export const ROLE_INFO: Record<
  AdminRole,
  { name: string; description: string }
> = {
  super_admin: {
    name: 'Super Admin',
    description: 'Full platform access, can manage other admins',
  },
  admin: {
    name: 'Admin',
    description: 'Full operational access except team management',
  },
  moderator: {
    name: 'Moderator',
    description: 'Content moderation and bot management only',
  },
  support: {
    name: 'Support',
    description: 'Read-only access to users/bots for troubleshooting',
  },
};

/**
 * All admin roles in order of hierarchy (highest first)
 */
export const ADMIN_ROLES: AdminRole[] = [
  'super_admin',
  'admin',
  'moderator',
  'support',
];

/**
 * Check if a role has a specific permission
 *
 * @param role - The admin role to check
 * @param permission - The permission to check for
 * @returns true if the role has the permission
 */
export function hasPermission(
  role: AdminRole | null | undefined,
  permission: Permission
): boolean {
  if (!role) return false;
  return ROLE_PERMISSIONS[role]?.includes(permission) ?? false;
}

/**
 * Check if a role has any of the specified permissions
 *
 * @param role - The admin role to check
 * @param permissions - Array of permissions to check for
 * @returns true if the role has at least one of the permissions
 */
export function hasAnyPermission(
  role: AdminRole | null | undefined,
  permissions: Permission[]
): boolean {
  if (!role) return false;
  return permissions.some((permission) => hasPermission(role, permission));
}

/**
 * Check if a role has all of the specified permissions
 *
 * @param role - The admin role to check
 * @param permissions - Array of permissions to check for
 * @returns true if the role has all of the permissions
 */
export function hasAllPermissions(
  role: AdminRole | null | undefined,
  permissions: Permission[]
): boolean {
  if (!role) return false;
  return permissions.every((permission) => hasPermission(role, permission));
}

/**
 * Get all permissions for a role
 *
 * @param role - The admin role
 * @returns Array of permissions the role has
 */
export function getPermissionsForRole(role: AdminRole): Permission[] {
  return ROLE_PERMISSIONS[role] ?? [];
}

/**
 * Get permissions grouped by category
 *
 * @param role - The admin role
 * @returns Permissions grouped by category
 */
export function getPermissionsByCategory(
  role: AdminRole
): Record<PermissionCategory, Permission[]> {
  const permissions = getPermissionsForRole(role);
  const grouped: Record<PermissionCategory, Permission[]> = {
    users: [],
    bots: [],
    moderation: [],
    costs: [],
    analytics: [],
    audit: [],
    team: [],
  };

  for (const permission of permissions) {
    const info = PERMISSIONS[permission];
    if (info) {
      grouped[info.category].push(permission);
    }
  }

  return grouped;
}

/**
 * Check if a role can manage another role (for role changes)
 * Only super_admin can manage any role
 *
 * @param actorRole - The role of the admin performing the action
 * @param targetRole - The role being managed
 * @returns true if the actor can manage the target role
 */
export function canManageRole(
  actorRole: AdminRole | null | undefined,
  targetRole: AdminRole
): boolean {
  if (!actorRole) return false;
  // Only super_admin can manage roles
  return actorRole === 'super_admin';
}

/**
 * Validate if a role string is a valid AdminRole
 *
 * @param role - The role string to validate
 * @returns true if the role is valid
 */
export function isValidRole(role: string): role is AdminRole {
  return ADMIN_ROLES.includes(role as AdminRole);
}
